import {
  AccountData,
  CustomTicketV2,
  Event,
  EventType,
  FormAnswersV2,
  FormCheckBoxField,
  FormInputType,
  FormRadioField,
  FormResponseV2,
  SavedFormQuestion,
  SavedFormQuestionState,
  SavedFormResponse,
} from "@markit/common.types";
import {
  getFormResponsesRef,
  getFormResponsesSnap,
  getUserData,
  getUserSavedFormQuestionData,
  getUserSavedFormResponsesRef,
} from "../FirebaseUtils";
import {
  db,
  getCountFromServer,
  getDocs,
  query,
  where,
  collectionGroup,
  ref,
  getMetadata,
  firebaseStorage,
} from "../../firebase";
import { withFirestoreTimeout } from "../firestoreTimeout";
import { generate } from "shortid";
import {
  extractUniqueFormQuestionIdsFromCustomTickets,
  filterUndefinedValues,
  getFileResponseBaseStoragePath,
  isFirebaseStorageUrl,
  isValidEmail,
  uniqueVals,
} from "@markit/common.utils";
import { saveMediaToFirestore } from "../photoUtils";
import { API_BASE } from "../../API";
import { ErrorCode } from "@markit/common.api";
import { DataQuestionFilterType } from "../../components/Form/SavedQuestionTypeDropdown";
import { makeEmptyEvent } from "../makeEvent";

// Check if the form question inputType is one with options vs. just a single option (ie. placeholder)
export const formQuestionHasOptions = (inputType: FormInputType) => {
  return (
    inputType === FormInputType.RADIO || inputType === FormInputType.CHECK_BOX
  );
};

// for getting responses to all questions for an event
export const getEventFormResponses = async (
  eventId: string
): Promise<FormResponseV2[]> => {
  const formResponseSnap = await getFormResponsesSnap(eventId);

  const formResponses = await Promise.all(
    formResponseSnap.docs.map((snapshot) => {
      const formResponseData = snapshot.data();
      return formResponseData;
    })
  );

  return formResponses;
};

export const getFormResponsesByUser = async (
  eventId: string,
  userId: string
): Promise<FormResponseV2 | undefined> => {
  const formResponsesRef = getFormResponsesRef(eventId);
  const query_ = query(formResponsesRef, where("uid", "==", userId));
  const querySnapshot = await getDocs(query_);
  if (querySnapshot.docs.length === 0) {
    console.log("no form responses found for user: ", userId);
    return undefined;
  }
  const formResponse = querySnapshot.docs.map((doc) => doc.data())[0];
  return formResponse;
};

// gets a specific answer from all user form responses for an event
export const getFormResponseAnswers = async (
  eventId: string,
  questionId: string
): Promise<FormAnswersV2[]> => {
  const userFormResponses = getFormResponsesRef(eventId);
  const query_ = query(
    userFormResponses,
    where(`answers.${questionId}`, "!=", "")
  );
  const querySnapshot = await getDocs(query_);

  if (querySnapshot.docs.length === 0) {
    console.log("no form responses found for question: ", questionId);
    return [];
  }
  const formAnswers = querySnapshot.docs.map((doc) => doc.data().answers);
  return formAnswers;
};

export const questionInputName = (inputType: FormInputType) =>
  inputType === FormInputType.RADIO
    ? "Multiple Choice"
    : inputType === FormInputType.CHECK_BOX
      ? "Checkbox"
      : inputType === FormInputType.TEXT_INPUT
        ? "Short Answer"
        : inputType === FormInputType.FILE_UPLOAD
          ? "File Upload"
          : "Collect Emails";

// for getting the number of responses for a specific question for an event
// Need to subtract the requested tickets (still pending) (TODO)
export const getNumFormQuestionResponses = async (
  eventId: string,
  questionId: string,
  inputType: FormInputType
): Promise<number> => {
  const userFormResponses = getFormResponsesRef(eventId);
  const query_ = query(
    userFormResponses,
    where(
      `answers.${questionId}`,
      "!=",
      inputType === FormInputType.EMAIL ||
        inputType === FormInputType.TEXT_INPUT ||
        inputType === FormInputType.FILE_UPLOAD
        ? ""
        : []
    )
  );

  // const ticketsRef = FirebaseUtils.getTicketsRef(eventId);
  // const requestedTicketsQuery = query(
  //   ticketsRef,
  //   where('requestStatus', '==', RequestStatus.PENDING)
  // );
  // const requestedSnapshot = await getCountFromServer(requestedTicketsQuery);
  // const requestedCount = requestedSnapshot.data().count;

  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

// Gets the form questions that are related to the specified event
export const getEventFormQuestions = async (
  event: Event
): Promise<SavedFormQuestion[]> => {
  const questionIds = extractUniqueFormQuestionIdsFromCustomTickets(event);

  const formQuestions = await withFirestoreTimeout(() =>
    Promise.all(
      questionIds.map(async (questionId) => {
        const savedFormQuestionData = await getUserSavedFormQuestionData(
          event.createdBy,
          questionId
        );
        if (savedFormQuestionData) {
          return savedFormQuestionData;
        }
      })
    )
  );
  return filterUndefinedValues(formQuestions);
};

// Gets the form questions that are related to the specified custom tickets
export const getEventCustomTicketFormQuestions = (
  customTickets: CustomTicketV2[],
  savedFormQuestions: SavedFormQuestion[]
): SavedFormQuestion[] => {
  const uniqueFormQuestionIds = uniqueVals(
    customTickets.flatMap((customTicket) => customTicket.formQuestions)
  );
  return filterUndefinedValues(
    uniqueFormQuestionIds.map((formQuestionId) =>
      savedFormQuestions.find(
        (formQuestion) => formQuestion.id === formQuestionId
      )
    )
  );
};

// Gets the form responses of a specified user that is related to an event
export const getUsersFormResponses = async (
  hostId: string,
  formQuestions: SavedFormQuestion[],
  userId: string
): Promise<{
  formResponses: SavedFormResponse[];
  fileNameRecords: Record<string, string>;
}> => {
  const formResponsesRef = getUserSavedFormResponsesRef(hostId, userId);
  const formResponses = await Promise.all(
    formQuestions.map(async (question) => {
      const query_ = query(
        formResponsesRef,
        where("questionId", "==", question.id)
      );
      const snapshot = await getDocs(query_);
      const formResponse = snapshot.docs.map((doc) => doc.data())[0];
      return formResponse;
    })
  );
  const definedFormResponses: SavedFormResponse[] =
    filterUndefinedValues(formResponses);

  const fileFormResponses = definedFormResponses.filter((formResponse) =>
    formQuestions.some(
      (question) =>
        question.formInput.inputType === FormInputType.FILE_UPLOAD &&
        question.id === formResponse.questionId
    )
  );
  const fileNameRecords = await getAllFileNamesFromResponses(fileFormResponses);
  return { formResponses: definedFormResponses, fileNameRecords };
};

// Gets the form questions that are related to the specified profile
export const getProfileFormQuestions = async (
  userId: string
): Promise<SavedFormQuestion[]> => {
  const userData = await getUserData(userId);
  if (userData && userData.formQuestions.length > 0) {
    const profileFormQuestions = await Promise.all(
      userData.formQuestions.map(
        async (question) => await getUserSavedFormQuestionData(userId, question)
      )
    );
    const profileFormQuestionsDefined: SavedFormQuestion[] =
      filterUndefinedValues(profileFormQuestions);
    return profileFormQuestionsDefined;
  }

  return [];
};

// An invalid option is one that is not part of the options, not part of inactive options or hasOtherOption is false
const hasInvalidOptionAnswers = (
  question: SavedFormQuestion,
  formAnswers: string[]
) => {
  const input = question.formInput as FormCheckBoxField | FormRadioField;
  const options = input.options || [];
  const inactiveOptions = input.inactiveOptions || [];
  const hasOtherOption = Boolean(input.hasOtherOption);
  if (formAnswers.some((answer) => answer.trim() === "")) {
    return true;
  }
  return formAnswers.some(
    (answer) =>
      !options.includes(answer) &&
      (inactiveOptions.includes(answer) || !hasOtherOption)
  );
};

export const validateEventForm = (
  answers: FormAnswersV2,
  eventFormQuestions: SavedFormQuestion[]
) => {
  const errorIds = eventFormQuestions
    .filter((question) => {
      const formAnswers = answers[question.id];
      if (formAnswers !== undefined && formAnswers.length > 0) {
        switch (question.formInput.inputType) {
          case FormInputType.TEXT_INPUT:
          case FormInputType.FILE_UPLOAD: {
            return (
              question.formInput.isRequired &&
              formAnswers.every((answer) => answer === "")
            );
          }
          case FormInputType.RADIO:
            return hasInvalidOptionAnswers(question, formAnswers);
          case FormInputType.CHECK_BOX: {
            return hasInvalidOptionAnswers(question, formAnswers);
          }
          case FormInputType.EMAIL: {
            return !isValidEmail(formAnswers[0]);
          }
          default: {
            return true;
          }
        }
      } else {
        return question.formInput.isRequired;
      }
    })
    .map((question) => question.id);

  return errorIds;
};

/** Helps store the file responses into firebase storage for FormUploadFile questions **/
export const finalizeFormFileUploadAnswers = async (params: {
  creatorUid: string;
  answers: FormAnswersV2;
  formQuestions: SavedFormQuestion[];
  fileNamesByQuestionId: Record<string, string>;
  setAnswers?: (answers: FormAnswersV2) => void;
  answersRef?: { current: FormAnswersV2 };
  eventId?: string;
  followerUid?: string; // No followerUid means we store the files under the event creator's uid
}): Promise<FormAnswersV2> => {
  const {
    creatorUid,
    answers,
    formQuestions,
    fileNamesByQuestionId,
    eventId,
    followerUid,
    setAnswers,
    answersRef,
  } = params;
  const next: FormAnswersV2 = { ...answers };
  const storageOwnerUid = followerUid || creatorUid;

  for (const question of formQuestions) {
    if (question.formInput.inputType !== FormInputType.FILE_UPLOAD) {
      continue;
    }
    const row = next[question.id];
    if (!row || row.length === 0) {
      continue;
    }
    const url = (row[0] || "").trim();
    if (!url || isFirebaseStorageUrl(url)) {
      continue;
    }

    const fileName = fileNamesByQuestionId[question.id] || "file";
    const basePath = getFileResponseBaseStoragePath(
      creatorUid,
      storageOwnerUid,
      question.id,
      eventId
    );
    const path = `${basePath}/${generate()}`;

    const downloadUrl = await saveMediaToFirestore(url, path, "", fileName);
    next[question.id] = [downloadUrl];
  }

  setAnswers?.(next);
  if (answersRef) {
    answersRef.current = next;
  }

  return next;
};

export const exportEventData = async (
  event: Event,
  isDownloading: boolean,
  setDownloading: (downloading: boolean) => void,
  onFinish?: (success: boolean) => void
) => {
  if (isDownloading) {
    return;
  }
  if (event) {
    setDownloading(true);

    let downloadUrl: string = "";
    downloadUrl = `${API_BASE}events/${event.id}/form-response?csv=true`;

    try {
      const response = await fetch(downloadUrl);

      if (response.status === ErrorCode.OK) {
        const blob = await response.blob();
        const anchor = document.createElement("a");
        anchor.href = window.URL.createObjectURL(blob);
        anchor.download = "form-response.csv";
        anchor.click();
      } else {
        throw Error;
      }
      if (onFinish) {
        onFinish(true);
      }
    } catch (error) {
      console.log("Error downloading file:", error);
      if (onFinish) {
        onFinish(false);
      }
    }
  }
  setDownloading(false);
};

export const exportIndividualFollowerData = async (
  uid: string,
  follower: AccountData,
  onFinish?: (success: boolean) => void
) => {
  try {
    let downloadUrl: string = "";
    downloadUrl = `${API_BASE}user/${uid}/${follower.uid}/exportIndividualFollowerData`;
    const response = await fetch(downloadUrl, {
      method: "POST",
    });

    // replace spaces with "-"
    const followerNameWithoutSpaces = follower.fullName.replace(/\s+/g, "-");

    if (response.status === ErrorCode.OK) {
      const blob = await response.blob();
      const anchor = document.createElement("a");
      anchor.href = window.URL.createObjectURL(blob);
      anchor.download = `${followerNameWithoutSpaces}-form-response.csv`;
      anchor.click();
    } else {
      throw Error;
    }
    if (onFinish) {
      onFinish(true);
    }
  } catch (error) {
    console.log("Error downloading file:", error);
    if (onFinish) {
      onFinish(false);
    }
  }
};

export const exportAllFollowerData = async (
  uid: string,
  onExportFinish?: (success: boolean) => void
) => {
  try {
    let downloadUrl: string = "";
    downloadUrl = `${API_BASE}user/${uid}/exportAllFollowerData`;

    const response = await fetch(downloadUrl, {
      method: "POST",
    });

    if (response.status === ErrorCode.OK) {
      const blob = await response.blob();
      const anchor = document.createElement("a");
      anchor.href = window.URL.createObjectURL(blob);
      anchor.download = "all-markit-follower-data.csv";
      anchor.click();
      if (onExportFinish) {
        onExportFinish(true);
      }
    } else {
      throw Error;
    }
  } catch (error) {
    console.log("Error downloading file:", error);
    if (onExportFinish) {
      onExportFinish(false);
    }
  }
};

// Get the number of responses for a specific question
export const getNumSavedQuestionResponses = async (
  userId: string,
  formQuestionId: string
) => {
  const group = collectionGroup(db, "savedFormResponses");
  const query_ = query(
    group,
    where("creatorUid", "==", userId),
    where("questionId", "==", formQuestionId),
    where("responses", "!=", [])
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

// Returns the saved form questions to display based on the filter state and active/inactive state
export const getSavedFormQuestionsToDisplay = (
  savedQuestionsToShow: SavedFormQuestion[],
  activeState: SavedFormQuestionState,
  filterState: DataQuestionFilterType
) => {
  let questions: SavedFormQuestion[] = savedQuestionsToShow.filter(
    (question) => question.state === activeState
  );

  questions = questions.filter((question) => {
    const inputType = question.formInput.inputType;
    if (filterState === DataQuestionFilterType.ANY_TYPE) {
      return true;
    }
    switch (inputType) {
      case FormInputType.TEXT_INPUT:
        return filterState === DataQuestionFilterType.SHORT_ANSWER;
      case FormInputType.RADIO:
        return filterState === DataQuestionFilterType.MULTIPLE_CHOICE;
      case FormInputType.CHECK_BOX:
        return filterState === DataQuestionFilterType.CHECKBOX;
      case FormInputType.EMAIL:
        return filterState === DataQuestionFilterType.EMAIL;
      case FormInputType.FILE_UPLOAD:
        return filterState === DataQuestionFilterType.FILE_UPLOAD;
      default:
        return false;
    }
  });

  return questions;
};

// Counts the number of occurrences of each option in the original options and inactive options
export const originalOptionCounts = (
  formQuestion: SavedFormQuestion | undefined
) => {
  if (!formQuestion) {
    return new Map<string, number>();
  }
  const {
    options: originalOptions = [],
    inactiveOptions: originalInactiveOptions = [],
  } = formQuestion.formInput as FormCheckBoxField | FormRadioField;
  return originalOptions
    .concat(originalInactiveOptions)
    .reduce((acc, option) => {
      acc.set(option, (acc.get(option) || 0) + 1);
      return acc;
    }, new Map<string, number>());
};

// Checks if the form answer option is part of the original options
export const isOriginalOptionOccurrence = (
  formQuestion: SavedFormQuestion | undefined,
  options: string[],
  optionValue: string,
  optionIndex: number
) => {
  const originalCount =
    originalOptionCounts(formQuestion).get(optionValue) || 0;
  if (originalCount === 0) {
    return false;
  }
  const currentOccurrence = options
    .slice(0, optionIndex + 1)
    .filter((option) => option === optionValue).length;
  return currentOccurrence <= originalCount;
};

// Gets the file name from the firebase storage metadata for the fileResponse
export const getFileNameFromResponse = async (filePath: string) => {
  if (!filePath) {
    return undefined;
  }
  const fileRef = ref(firebaseStorage, filePath);
  const metadata = await getMetadata(fileRef);
  return metadata.customMetadata?.fileName;
};

// Gets the file names for all the file upload questions for a user
// Returns a record of the file names by question id
export const getAllFileNamesFromResponses = async (
  formResponses: SavedFormResponse[]
) => {
  const fileNameRecords: Record<string, string> = {};
  await Promise.all(
    formResponses.map(async (formResponse) => {
      const fileName = await getFileNameFromResponse(formResponse.responses[0]);
      if (fileName) {
        fileNameRecords[formResponse.questionId] = fileName;
      }
    })
  );
  return fileNameRecords;
};

export const getSubscribeCampaignAsForm = (
  userId: string,
  profilePicURL: string,
  isAdmin: boolean
) => {
  const emptyEvent: Event = {
    ...makeEmptyEvent(userId, isAdmin, undefined, EventType.GENERIC_LINK),
    title: "Profile Subscribe Form",
    mediaURLs: [profilePicURL],
  };
  return emptyEvent;
};
